import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageLayout, Input, Button } from "../../ui";
import { useAuthContext } from "../auth/hooks";
import { useImpersonation } from "./hooks";
import { listImpersonationCustomers } from "./api";

/**
 * Staff-only "View as customer" page. Pick (or type) a customer domain to load
 * the app, read-only, exactly as that customer sees it. The global banner
 * (rendered by PageLayout) shows the active target and an Exit control.
 */
export function ImpersonatePage() {
  const { isStaff, isLoading } = useAuthContext();
  const { impersonationDomain, startImpersonation, stopImpersonation } =
    useImpersonation();

  const [customers, setCustomers] = useState<string[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!isStaff) return;
    let cancelled = false;
    (async () => {
      setLoadingCustomers(true);
      const list = await listImpersonationCustomers();
      if (!cancelled) {
        setCustomers(list);
        setLoadingCustomers(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isStaff]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return customers;
    return customers.filter((d) => d.includes(q));
  }, [customers, query]);

  // Only Composo staff may reach this page. (The backend re-checks before
  // honoring any impersonation header, so this is a UX gate, not the guard.)
  if (!isLoading && !isStaff) {
    return <Navigate to="/insights" replace />;
  }

  const typed = query.trim().toLowerCase();
  const showTypedOption = typed.length > 0 && !customers.includes(typed);

  return (
    <PageLayout>
      <div className="mx-auto max-w-2xl p-8">
        <h1 className="text-xl font-semibold text-foreground">View as customer</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Load the app read-only, exactly as a customer sees it. Staff only;
          writes are disabled while viewing.
        </p>

        {impersonationDomain && (
          <div className="mt-4 flex items-center justify-between rounded-md border border-border bg-muted px-4 py-3 text-sm">
            <span>
              Currently viewing as <strong>{impersonationDomain}</strong>
            </span>
            <Button onClick={stopImpersonation}>Stop</Button>
          </div>
        )}

        <div className="mt-6">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search or type a customer domain (e.g. acme.com)"
          />
        </div>

        <div className="mt-3 max-h-96 overflow-y-auto rounded-md border border-border">
          {loadingCustomers ? (
            <div className="p-4 text-sm text-muted-foreground">
              Loading customers…
            </div>
          ) : filtered.length > 0 || showTypedOption ? (
            <>
              {filtered.map((domain) => (
                <button
                  key={domain}
                  onClick={() => startImpersonation(domain)}
                  className="flex w-full items-center justify-between border-b border-border px-4 py-2 text-left text-sm last:border-b-0 hover:bg-accent"
                >
                  <span>{domain}</span>
                  <span className="text-xs text-muted-foreground">View as →</span>
                </button>
              ))}
              {showTypedOption && (
                <button
                  onClick={() => startImpersonation(typed)}
                  className="flex w-full items-center justify-between border-t border-border px-4 py-2 text-left text-sm hover:bg-accent"
                >
                  <span>
                    View as <strong>{typed}</strong>
                  </span>
                  <span className="text-xs text-muted-foreground">View as →</span>
                </button>
              )}
            </>
          ) : (
            <div className="p-4 text-sm text-muted-foreground">
              No customers found.
            </div>
          )}
        </div>
      </div>
    </PageLayout>
  );
}
